import { z } from "zod";
import { IDBActivityCartItem } from "./activityCart.types";

export interface IGetTourOpitonParams {
  tourOptionId: number;
  selectedmarketId: number;
  transferType?: TransferTypeEnum;
  noOfAdults: number;
  noOfChildren: number;
  activityDate: string;
  sharingBasis?: sharingBasisEnum;
}

export interface DmcKartPayload {
  cartItemId: number;
  itemType: string;
  hotel: {
    internalRemarks?: string;
    customerRemarks?: string;
    checkInDate?: string;
    noOfNights?: number;
  };
  tour: {
    internalRemarks?: string;
    customerRemarks?: string;
    startDate?: string;
  };
  transfer: {
    internalRemarks?: string;
    customerRemarks?: string;
    firstPickUpDate?: string;
    secondPickUpDate?: string;
  };
}

export enum cartItemTypesEnum {
  activity = "Activity",
  transfer = "Transfer",
  hotel = "Hotel",
  miscellaneous = "Miscellaneous",
}
export enum TransferTypeEnum {
  oneWay = "OneWay",
  return = "Return",
}
export enum ISharingBasisEnum {
  sharing = "SIC",
  private = "PVT",
}

export enum sharingBasisEnum {
  sharing = "SIC",
  private = "PVT",
  dp_sic_pk_sic = "DROP_SIC_PICKUP_SIC",
  dp_sic_pk_pvt = "DROP_SIC_PICKUP_PVT",
  dp_pvt_pk_sic = "DROP_PVT_PICKUP_SIC",
  dp_pvt_pk_pvt = "DROP_PVT_PICKUP_PVT",
}

export const zodDMCComboTourOption = z.object({
  tourOptionId: z.number(),
  onwardTransfer: z
    .enum([sharingBasisEnum.private, sharingBasisEnum.sharing])
    .nullish(),
  returnTransfer: z
    .enum([sharingBasisEnum.private, sharingBasisEnum.sharing])
    .nullish(),
});

export const zodDMCCombo = z.object({
  id: z.number().nullish(),
  name: z.string(),
  amount: z.number(),
  tourOptions: z.array(zodDMCComboTourOption),
});

export type TDMCComboTourOption = z.infer<typeof zodDMCComboTourOption>;
export type TDMCCombo = z.infer<typeof zodDMCCombo>;

export interface IWorldCity {
  id: number;
  city: string;
  city_ascii: string;
  lat: number;
  lon: number;
  country: string;
  country_iso2: string;
  country_iso3: string;
  simpleMaps_id: number;
}

export interface IDMCActivity {
  id: number;
  name: string;
  description: string;
  cityId: number;
  canAddMultiTour?: boolean;
  isTransferRequired: boolean;
  defaultPickUpTransferId: number;
  defaultDropTransferId: number;
  isBounded: boolean;
  mongo_siteId: string;
  createdByUserId: number;
  createdAt: Date;
  updatedAt: Date;
  city: IWorldCity;
  priority?: number;
  isActive: boolean;
  area: IArea;
  activityImages: IActivityImages[];
}

export interface IArea {
  id: number;
  name: string;
  fromTransfers: IAreasTransfers[];
  toTransfers: IAreasTransfers[];
  city: IWorldCity;
  activities: IDMCActivity[];
}

export interface IAreasTransfers {
  id: number;
  description: string | null;
  marketId: number;
  market: number;
  from: IArea;
  isCustom: boolean;
  to: IArea;
  pickUpActivityCartItems: IDBActivityCartItem[];
  dropActivityCartItems: IDBActivityCartItem[];
  mongoDbSiteId: string;
  boundTourOptionId: number;
  boundTourOption?: IDMCTourOption;
  transferType: TransferTypeEnum;
  sharingBasis: ISharingBasisEnum;
  amount?: number;
  customerAmount: number;
  isStandAlone: boolean;
  isPerPax: boolean;
  minPax: number;
  maxPax: number;
  validFromDate: Date;
  validTillDate: Date;
  createdByUserId: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface IDMCActivityWTourOptions extends IDMCActivity {
  tourOptions: IDMCTourOption[];
}

export interface IDMCActivityWithTourOptionsAndRates
  extends IDMCActivityWTourOptions {
  tourOptions: IDMCTourOptionWithRates[];
}

export interface IDMCTourOption {
  id?: number;
  name: string;
  description: string;
  hasTimeSlots: boolean;
  activityImages: IActivityImages[];
  hasMeal: boolean;
  hasGuide: boolean;
  hasTicket: boolean;
  sort_no: number;
  pickupPickupTime?: number;
  dropPickupTime?: number;
  activityId?: number;
  pickUpAreaTransfer: IAreasTransfers;
  dropAreaTransfer: IAreasTransfers;
  returnAreaTransfer: IAreasTransfers;
}

interface TourOptionActivity {
  activity: IDMCActivity;
}

export interface IDMCTourOptionWithActivity
  extends IDMCTourOption,
    TourOptionActivity {}

export interface IDMCTourOptionWithRates
  extends IDMCTourOption,
    TourOptionActivity {
  transferRates: ITourOptionTransferRate[];
  ticketRates: ITourOptionTicketRate[];
}

export interface ITourOptionTransferRate {
  id: number;
  tourOptionId: number;
  marketId: number;
  mongoDbSiteId: string;
  amount: number;
  isPerPax: boolean;
  currencyCode: string;
  validFromDate: Date;
  validTillDate: Date;
  priority: number;
  createdByUserId: number;
  createdAt: Date;
  updatedAt: Date;
  description: string;
  transferType: string;
  sharingBasis: string;
  minPax: number;
  maxPax: number;
}

export interface ITourOptionTicketRate {
  id: number;
  tourOptionId: number;
  marketId: number;
  mongoDbSiteId: string;
  adultAmount?: number;
  customerAdultAmount: number;
  childAmount?: number;
  customerChildAmount: number;
  isPerPax: boolean;
  validFromDate: Date;
  validTillDate: Date;
  priority: number;
  createdByUserId: number;
  createdAt: Date;
  updatedAt: Date;
}

export enum transferTypePickupDropEnum {
  pickup = "Pickup",
  drop = "Drop",
  pickupAndDrop = "Pickup & Drop",
}

export interface IActivityImages {
  id?: number;
  image_url: string;
  activityId?: number;
  tourOptionId?: number;
  sort_no: number;
}
